import React, {useRef, useState, useCallback} from 'react';
import {Box, IconButton} from '@mui/material';
import {useReactToPrint} from 'react-to-print';
import PrintIcon from '@mui/icons-material/Print';
import {useLazyQuery} from '@apollo/client';
import {Button} from '../../components/Button/Button';
import {GET_RISK_AND_NOTES} from './riskQueries';
import {Prompt} from '../../components/Prompt/Prompt';
import {PrintableRiskFormInner} from './PrintableRiskForm';
import {
  GetRiskAndNotes,
  GetRiskAndNotesVariables,
} from '../../schema-types/GetRiskAndNotes';
import {ReviewNotesPrinted} from '../notes/PrintableNotes';
import {PrintStyles} from './PrintStyles';
import validateUUID from '../../utils/validateUUID';

/**
 * Main component that handles data fetching and printing in one sequence
 */
export const PrintRiskAndNotes: React.FC<{
  accountId: string;
  reviewId: string;
  accountName: string;
  isTrust: boolean;
  contactId?: string;
}> = ({accountId, isTrust, reviewId, accountName, contactId}) => {
  const [printData, setPrintData] = useState<GetRiskAndNotes | null>(null);

  const componentRef = useRef<HTMLDivElement | null>(null);

  const [fetchRiskAndNotes, {loading, error}] = useLazyQuery<
    GetRiskAndNotes,
    GetRiskAndNotesVariables
  >(GET_RISK_AND_NOTES, {
    fetchPolicy: 'network-only', // Always fetch fresh data from network
    onCompleted: (data) => {
      // Set the data and it will trigger the print effect
      setPrintData(data);
    },
    // eslint-disable-next-line @typescript-eslint/no-shadow
    onError: (error) => {
      console.error('Error fetching risk data for printing:', error);
    },
  });

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Risk Assessment - ${
      printData?.reviewDetails?.contacts?.[0]?.simplifiedName || accountName || 'Client'
    }`,
    onAfterPrint: () => {
      // Clear the print data after printing is complete
      setPrintData(null);
    },
  });

  // Handle the print button click
  const onPrintClick = useCallback(() => {
    // Clear any existing print data first
    setPrintData(null);

    // Fetch fresh data
    fetchRiskAndNotes({
      variables: {
        reviewId: validateUUID(reviewId)!,
        accountId: validateUUID(accountId)!,
        contactId: validateUUID(contactId)!,
      },
    });
  }, [fetchRiskAndNotes, reviewId, accountId, contactId]);

  // Effect to trigger print once data is loaded and component is rendered
  React.useEffect(() => {
    if (printData?.reviewDetails && componentRef.current) {
      // Small delay to ensure the component is fully rendered
      const timer = setTimeout(() => {
        handlePrint();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [printData, handlePrint]);

  // Show error state if fetch failed
  if (error) {
    return (
      <Box mb={2}>
        <Prompt
          variant="error"
          title="Error loading risk data for printing"
          content={error.message}
        />
        <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
          <Button
            startIcon={
              <IconButton
                aria-label="print"
                color="secondary"
                size="large"
                sx={{margin: 0}}
              >
                <PrintIcon />
              </IconButton>
            }
            eventName="Print risk and notes"
            onClick={onPrintClick}
            variant="text"
          >
            Print Risk and Notes
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Hidden component that gets rendered with fresh data for printing */}
      {printData?.reviewDetails && (
        <div style={{display: 'none'}}>
          <PrintableRiskAndNotes
            ref={componentRef}
            accountId={accountId}
            reviewId={reviewId}
            accountName={accountName}
            contactId={contactId}
            data={printData}
            isTrust={isTrust}
          />
        </div>
      )}

      <Box display="flex" alignItems="center" justifyContent="flex-end">
        <IconButton
          aria-label="print"
          color={loading ? 'default' : 'primary'}
          size="large"
          sx={{margin: 0}}
          disabled={loading}
        >
          <PrintIcon onClick={onPrintClick} />
        </IconButton>
      </Box>
    </Box>
  );
};

const PrintableRiskAndNotes = React.forwardRef<
  any,
  {
    accountId: string;
    reviewId: string;
    accountName: string;
    contactId?: string;
    data: GetRiskAndNotes;
    isTrust: boolean;
  }
>(({accountId, reviewId, accountName, contactId, data, isTrust}, ref) => {
  return (
    <div ref={ref}>
      <PrintStyles />
      <ReviewNotesPrinted handoverData={data} />
      {isTrust ? null : (
        <>
          <div className="page-break" />
          <PrintableRiskFormInner
            accountName={accountName || 'Client'}
            risk={data}
            accountId={accountId || ''}
          />
        </>
      )}
    </div>
  );
});

PrintableRiskAndNotes.displayName = 'PrintableRiskAndNotes';
